'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

/**
 * Single source of truth for the footer/hamburger navigation link sets.
 *
 * Before this module, three surfaces (marketing header hamburger,
 * FloatingHeaderControls hamburger ×2, SidebarMenuContent) each hand-rolled
 * their own `footerLinks` + `legalLinks` arrays and they drifted — the legacy
 * header had App/Blog/For-professionals, the floating header had How-we-rank,
 * the sidebar menu had neither. Every consumer now reads this one config.
 */

/**
 * Static app routes the footer config may link to. Every literal must exist
 * in `routing.pathnames` — the typed-routes `<Link href>` in
 * SidebarMenuContent fails the build if one is renamed/removed.
 */
type FooterHref =
  | '/chat'
  | '/security'
  | '/why-lexomat'
  | '/pricing'
  | '/for-professionals'
  | '/for-lawyers'
  | '/faq'
  | '/blog'
  | '/about'
  | '/contact'
  | '/terms-of-use'
  | '/privacy-policy'
  | '/how-we-rank';

/**
 * Discriminated on `isExternal` so consumers can pass internal hrefs to the
 * typed-routes `<Link>` without casts and external hrefs to a plain `<a>`.
 */
interface InternalFooterNavLink {
  href: FooterHref;
  label: string;
  isExternal: false;
}

interface ExternalFooterNavLink {
  href: string;
  label: string;
  isExternal: true;
}

type FooterNavLink = InternalFooterNavLink | ExternalFooterNavLink;

/**
 * Route prefixes where the marketing hamburger menu is shown. Union of the
 * two previously-drifted per-header lists (header-client vs
 * FloatingHeaderControls) so both headers gate identically.
 */
export const HAMBURGER_MENU_PAGES = [
  '/about',
  '/why-lexomat',
  '/security',
  '/terms-of-use',
  '/privacy-policy',
  '/pricing',
  '/contact',
  '/faq',
  '/blog',
  '/for-professionals',
  // Advocate product landing page (#7230). Listing the prefix here is
  // unconditional — it only decides whether the marketing hamburger is shown
  // ON that page, which is moot while the route 404s.
  '/for-lawyers',
  '/how-we-rank',
] as const;

/**
 * Footer/menu link sets, translated. Static `tFooter('…')` calls (not dynamic
 * key lookups) so the next-intl orphan scanner keeps seeing every key.
 *
 * `includeAppLink` prepends the "App" (/chat) link — wanted on marketing
 * surfaces where the user is outside the app, redundant inside the app shell.
 */
export function useFooterNavLinks({
  includeAppLink = false,
  includeAdvocateLanding = false,
}: {
  includeAppLink?: boolean;
  /**
   * Include the advocate landing page (#7230) link — launch checklist item 3
   * (#7951). Sits at the head of `footerLinks`, after the optional App link.
   * Off by default: while `ADVOCATE_LANDING_ENABLED` is unset the
   * route 404s, and a menu entry pointing at a 404 is worse than no entry.
   * The value is a build-time, server-only env, so it arrives as a prop from
   * the server component that renders the header (see `elements/header.tsx`)
   * rather than being read from `process.env` in this client module.
   */
  includeAdvocateLanding?: boolean;
} = {}): {
  footerLinks: readonly FooterNavLink[];
  /** Legal links are always internal routes — typed so `<Link>` needs no narrowing. */
  legalLinks: readonly InternalFooterNavLink[];
} {
  const tFooter = useTranslations('Footer');

  return useMemo(() => {
    const footerLinks = [
      ...(includeAppLink
        ? ([
            { href: '/chat', label: tFooter('app'), isExternal: false },
          ] as const)
        : []),
      ...(includeAdvocateLanding
        ? ([
            {
              href: '/for-lawyers',
              label: tFooter('forLawyers'),
              isExternal: false,
            },
          ] as const)
        : []),
      { href: '/security', label: tFooter('security'), isExternal: false },
      { href: '/why-lexomat', label: tFooter('whyLexomat'), isExternal: false },
      { href: '/pricing', label: tFooter('pricing'), isExternal: false },
      { href: '/faq', label: tFooter('faq'), isExternal: false },
      { href: '/blog', label: tFooter('blog'), isExternal: false },
      { href: '/about', label: tFooter('about'), isExternal: false },
      { href: '/contact', label: tFooter('contact'), isExternal: false },
      {
        href: 'https://status.lexomat.sk/',
        label: tFooter('status'),
        isExternal: true,
      },
    ] as const satisfies readonly FooterNavLink[];

    const legalLinks = [
      {
        href: '/terms-of-use',
        label: tFooter('termsOfUse'),
        isExternal: false,
      },
      {
        href: '/privacy-policy',
        label: tFooter('privacyPolicy'),
        isExternal: false,
      },
      {
        // Public ranking-transparency page required by zákon č. 108/2024 +
        // EU UCPD Art. 7(4a). Documents the lawyer-search ranking parameters
        // and discloses the Profil+ paid placement model. Issue #3688.
        href: '/how-we-rank',
        label: tFooter('howWeRank'),
        isExternal: false,
      },
    ] as const satisfies readonly InternalFooterNavLink[];

    return { footerLinks, legalLinks };
  }, [includeAppLink, includeAdvocateLanding, tFooter]);
}
